/**
 * Puente de impresión: iframe (sin popup) o agente local (sin diálogo).
 * En la app Windows, window.open('') dispara about:blank → Microsoft Store.
 */

import {
	loadPrintSettings,
	savePrintSettings,
	resolvePrintTarget,
	persistPaperWidth,
	agentRequestHeaders,
} from './print-settings.js';
import {
	buildThermalTicketText,
	buildThermalComandaText,
	buildThermalReceiptText,
	thermalTextToHtml,
	resolvePaperWidth,
} from './thermal-ticket.js';

const DARK_PRINT_CSS = `
html,body{background:#fff!important;color:#000!important;}
*{color:#000!important;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}
body{font-family:'Courier New',Courier,monospace!important;color:#000!important;}
`;

function isDesktop(win) {
	return !!(win && (win.POSKN_IS_DESKTOP || (win.posknDesktop && win.posknDesktop.isDesktop)));
}

function htmlToDocument(title, htmlBody, styles) {
	const css = styles || '';
	return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title || 'POSKN'}</title><style>${css}</style></head><body>${htmlBody}</body></html>`;
}

function hardenPrintHtml(fullHtml) {
	const html = String(fullHtml || '');
	if (/id=["']poskn-dark-print["']/.test(html)) return html;
	const tag = `<style id="poskn-dark-print">${DARK_PRINT_CSS}</style>`;
	if (/<\/head>/i.test(html)) return html.replace(/<\/head>/i, `${tag}</head>`);
	if (/<html[^>]*>/i.test(html)) return html.replace(/<html[^>]*>/i, (m) => `${m}<head>${tag}</head>`);
	return `<!DOCTYPE html><html><head><meta charset="UTF-8">${tag}</head><body>${html}</body></html>`;
}

function restoreAppFocus(win) {
	try {
		if (win.posknDesktop && typeof win.posknDesktop.restoreFocus === 'function') {
			win.posknDesktop.restoreFocus();
		}
	} catch (e) {
		/* ignore */
	}
	try {
		win.focus();
	} catch (e2) {
		/* ignore */
	}
	const ae = win.document && win.document.activeElement;
	if (ae && (ae.id === 'poskn-print-frame' || ae.tagName === 'IFRAME')) {
		const search = win.document.getElementById('buscar-productos');
		if (search) {
			try {
				search.focus();
			} catch (e3) {
				/* ignore */
			}
		}
	}
}

function printViaIframe(win, fullHtml, onSuccess) {
	return new Promise((resolve) => {
		const id = 'poskn-print-frame';
		const prev = win.document.getElementById(id);
		if (prev) {
			try {
				prev.remove();
			} catch (e) {
				/* ignore */
			}
		}
		const iframe = win.document.createElement('iframe');
		iframe.id = id;
		iframe.setAttribute('aria-hidden', 'true');
		iframe.setAttribute('tabindex', '-1');
		iframe.setAttribute('title', 'POSKN print');
		iframe.style.cssText =
			'position:fixed;left:-10000px;top:0;width:80mm;height:10mm;border:0;opacity:0;pointer-events:none;';
		let done = false;
		const finish = (ok) => {
			if (done) return;
			done = true;
			win.setTimeout(() => {
				try {
					iframe.remove();
				} catch (e) {
					/* ignore */
				}
				restoreAppFocus(win);
			}, 400);
			if (ok && typeof onSuccess === 'function') onSuccess();
			resolve(!!ok);
		};
		iframe.srcdoc = hardenPrintHtml(fullHtml);
		iframe.onload = () => {
			win.setTimeout(() => {
				if (done) return;
				try {
					const cw = iframe.contentWindow;
					if (cw) {
						cw.print();
					}
					finish(true);
				} catch (e) {
					if (typeof win.mostrarNotificacion === 'function') {
						win.mostrarNotificacion('Error al enviar a impresión', 'error');
					}
					finish(false);
				}
			}, 250);
		};
		win.document.body.appendChild(iframe);
		win.setTimeout(() => {
			if (done) return;
			try {
				const cw = iframe.contentWindow;
				if (cw) {
					cw.print();
				}
				finish(true);
			} catch (e2) {
				finish(false);
			}
		}, 1200);
	});
}

function printViaBrowser(win, title, fullHtml, onSuccess) {
	if (isDesktop(win) || !win.open) {
		return printViaIframe(win, fullHtml, onSuccess);
	}
	const ventanaImprimir = win.open('', '_blank', 'width=800,height=600');
	if (!ventanaImprimir) {
		return printViaIframe(win, fullHtml, onSuccess);
	}
	try {
		ventanaImprimir.document.write(hardenPrintHtml(fullHtml));
		ventanaImprimir.document.close();
	} catch (e) {
		try {
			ventanaImprimir.close();
		} catch (e2) {
			/* ignore */
		}
		return printViaIframe(win, fullHtml, onSuccess);
	}
	return new Promise((resolve) => {
		win.setTimeout(() => {
			try {
				ventanaImprimir.print();
				win.setTimeout(() => {
					try {
						ventanaImprimir.close();
					} catch (e) {
						/* ignore */
					}
				}, 1500);
				if (typeof onSuccess === 'function') onSuccess();
				resolve(true);
			} catch (e) {
				try {
					ventanaImprimir.close();
				} catch (e2) {
					/* ignore */
				}
				printViaIframe(win, fullHtml, onSuccess).then(resolve);
			}
		}, 400);
	});
}

async function printViaAgent(win, fullHtml, opts) {
	const cfg = loadPrintSettings();
	const url = (cfg.agentUrl || '').replace(/\/$/, '');
	const target = await resolvePrintTarget(cfg);
	const printerName = target.printerName || cfg.printerName || '';
	const paperWidth = resolvePaperWidth(
		target.remapped && target.inferredPaper
			? target.inferredPaper
			: (opts && opts.paperWidth) || cfg.paperWidth || target.paperWidth,
	);
	if (target.remapped && printerName) {
		const patch = { printerName };
		if (target.inferredPaper) patch.paperWidth = target.inferredPaper;
		savePrintSettings(patch);
		if (target.inferredPaper) persistPaperWidth(target.inferredPaper);
	}

	let connection = String(cfg.connection || 'system').toLowerCase();
	if (connection === 'bluetooth' || connection === 'usb' || connection === 'bt') {
		connection = 'system';
	}

	const payload = {
		html: fullHtml,
		text: (opts && opts.text) || '',
		title: (opts && opts.title) || 'POSKN',
		kind: (opts && opts.kind) || 'ticket',
		connection,
		printerName,
		printerIp: cfg.printerIp || '',
		printerPort: cfg.printerPort || 9100,
		paperWidth,
		dark: true,
	};

	const res = await fetch(`${url}/print`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', ...agentRequestHeaders(cfg) },
		body: JSON.stringify(payload),
	});
	const data = await res.json().catch(() => ({}));
	if (!res.ok || !data.ok) {
		throw new Error((data && data.error) || `HTTP ${res.status}`);
	}
	return data;
}

/**
 * Imprime HTML (documento completo o body).
 * @param {Window} win
 * @param {object} options
 * @param {string} options.title
 * @param {string} [options.html] documento HTML completo
 * @param {string} [options.body] fragmento body
 * @param {string} [options.styles]
 * @param {string} [options.text] texto plano térmico (preferido por el agente)
 * @param {string} [options.paperWidth] 58|80
 * @param {string} [options.kind] ticket|cierre|comanda|reporte
 * @param {Function} [options.onSuccess]
 * @param {boolean} [options.forceBrowser]
 */
export async function posknPrint(win, options) {
	const opts = options || {};
	const paperWidth = resolvePaperWidth(opts.paperWidth);
	const text = String((opts && opts.text) || '').trim();
	const kind = String((opts && opts.kind) || 'ticket').toLowerCase();
	const useThermalDoc =
		!!text && (kind === 'ticket' || kind === 'comanda' || kind === 'recibo' || kind === 'receipt');
	const fullHtml = useThermalDoc
		? thermalTextToHtml(opts.title || 'POSKN', text, paperWidth)
		: hardenPrintHtml(
				opts.html || htmlToDocument(opts.title || 'POSKN', opts.body || '', opts.styles || ''),
			);
	opts.paperWidth = paperWidth;
	const cfg = loadPrintSettings();
	const useAgent = !opts.forceBrowser && cfg.method === 'agent';

	if (useAgent) {
		try {
			const result = await printViaAgent(win, fullHtml, opts);
			if (typeof opts.onSuccess === 'function') opts.onSuccess();
			restoreAppFocus(win);
			if (typeof win.mostrarNotificacion === 'function') {
				const via = result && result.via ? ` · ${result.via}` : '';
				const prn = result && result.printer ? ` ${result.printer}` : '';
				win.mostrarNotificacion(`Ticket enviado a impresora${prn}${via}`, 'success');
			}
			return true;
		} catch (e) {
			console.warn('POSKN print agent falló:', e);
			const kind = opts.kind || 'ticket';
			if (kind === 'ticket' || kind === 'comanda') {
				if (typeof win.mostrarNotificacion === 'function') {
					win.mostrarNotificacion(
						`No se pudo imprimir el ticket: ${e && e.message ? e.message : e}`,
						'error',
					);
				}
				return false;
			}
			if (typeof win.mostrarNotificacion === 'function') {
				win.mostrarNotificacion(
					`Agente falló (${e && e.message ? e.message : e}). Abriendo impresión…`,
					'warning',
				);
			}
			return printViaBrowser(win, opts.title, fullHtml, opts.onSuccess);
		}
	}

	return printViaBrowser(win, opts.title, fullHtml, opts.onSuccess);
}

export function shouldAutoPrint(kind) {
	const cfg = loadPrintSettings();
	if (kind === 'ticket') return !!cfg.autoTicket;
	if (kind === 'cierre') return !!cfg.autoCierre;
	if (kind === 'comanda') return !!cfg.autoComanda;
	if (kind === 'reporte') return !!cfg.autoReporte;
	return false;
}

export function attachPrintBridgeGlobals(win) {
	win.posknPrint = (options) => posknPrint(win, options);
	win.posknShouldAutoPrint = shouldAutoPrint;
	win.posknBuildThermalTicketText = buildThermalTicketText;
	win.posknBuildThermalComandaText = buildThermalComandaText;
	win.posknBuildThermalReceiptText = buildThermalReceiptText;
	win.posknThermalTextToHtml = thermalTextToHtml;
	win.posknResolvePaperWidth = resolvePaperWidth;
	if (isDesktop(win) && !win._posknPrintTargetReady) {
		win._posknPrintTargetReady = true;
		resolvePrintTarget(loadPrintSettings())
			.then((target) => {
				if (!target.remapped || !target.printerName) return;
				const patch = { printerName: target.printerName };
				if (target.inferredPaper) {
					patch.paperWidth = target.inferredPaper;
					persistPaperWidth(target.inferredPaper);
				}
				savePrintSettings(patch);
			})
			.catch(() => {});
	}
}
